/**
 * A entrada — as nove telas do login que são nossas (H01).
 *
 * A senha NÃO passa por aqui. O login acontece no provedor de identidade da
 * revenda, um realm por revenda (ADR-0006); o que é nosso é a pergunta que
 * precede o login e todo desfecho que volta dele.
 *
 * Decisões, o que cada tela resolve e o que ficou em aberto:
 * `docs/design/login.md`. A marca: `docs/design/marca.md`.
 *
 * ── Uma tela, nove situações ──────────────────────────────────────────────
 *
 * As nove compartilham a mesma casca — cartão claro à esquerda, painel em
 * tinta à direita — e diferem só no miolo. Quebrar isso em nove componentes
 * faria a casca ser copiada nove vezes, e a nona esqueceria o painel.
 *
 * ── O que difere da entrega ───────────────────────────────────────────────
 *
 * As imagens vivem em `/marca/`, e não em `/assets/`: o Vite publica o bundle
 * em `/assets/`, e misturar o que é marca com o que é build confunde quem
 * limpa a pasta. O painel esconde a linha de contato cujo valor está vazio,
 * em vez de mostrar exemplo. `detalhe` é um acréscimo: o texto técnico da
 * falha, pequeno, embaixo da tela "fora do ar" — sem ele o suporte só teria o
 * código de correlação, e nem sempre há um.
 */

import type { ReactNode } from 'react';
import type { OrganizacaoDaPessoa } from '@cliqcrm/contracts';
import { Tabela } from '@/shared/ui';
import { nomeDoPapel } from '@/entities/acesso/papel';
import './entrada.css';

export type Situacao =
  /**
   * Entrar — e mais nada antes disso.
   *
   * Eram duas situações, `perguntar-revenda` e `confirmar-revenda`: com um
   * realm por revenda, o navegador precisava saber PARA ONDE mandar o login
   * antes de existir qualquer token, e a organização vinha primeiro.
   *
   * Com o realm compartilhado a senha é conferida num lugar só. A organização
   * deixou de ser pergunta de entrada e virou escolha de DEPOIS — feita na
   * tela própria, já sabendo em quais a pessoa entra.
   */
  | 'entrar'
  /**
   * Escolher a organização, depois da senha.
   *
   * É uma situação DESTA tela, e não uma tela nova, porque para quem entra é o
   * mesmo momento: o enquadramento, a marca e o painel de suporte continuam
   * onde estavam, e só o miolo troca. A credencial em si acontece entre as
   * duas, na página do provedor — que usa o nosso tema, pelo mesmo motivo.
   */
  | 'escolher'
  | 'voltando'
  | 'revenda-nao-existe'
  | 'provedor-fora'
  | 'sessao-expirada'
  | 'sem-vinculo'
  | 'desativado'
  | 'saiu';

export type Pessoa = {
  nome: string;
  apoio: string;
  iniciais: string;
};

export type Suporte = {
  /** Só dígitos com país, para o link do WhatsApp. Vazio esconde a linha. */
  whatsapp: string;
  whatsappVisivel: string;
  telefone: string;
  email: string;
  horario: string;
};

export type Incidente = {
  texto: string;
  desde: string;
};

export type EntradaProps = {
  situacao: Situacao;
  suporte: Suporte;
  /** Nome da revenda, quando já se sabe qual é. Usado só nas recusas. */
  revenda?: string;
  aoContinuar?: () => void;
  /** As organizações da pessoa, em `escolher`. */
  revendas?: readonly OrganizacaoDaPessoa[];
  aoEscolher?: (slug: string) => void;
  /** Qual está entrando, para as outras não aceitarem clique. */
  entrando?: string | null;
  /** Trava o botão sem apagar o que foi digitado. */
  indo?: boolean;
  /** Quem estava logado — `sessao-expirada`, `sem-vinculo`, `desativado`. */
  pessoa?: Pessoa;
  /** Para onde a pessoa volta ao reentrar. */
  destino?: string;
  /** Código de correlação, para o suporte achar a falha no registro. */
  correlationId?: string;
  /** O texto técnico da falha, quando existe. Só em `provedor-fora`. */
  detalhe?: string;
  /** Data em que o acesso foi desativado. */
  desativadoEm?: string;
  /** Aparece no cartão, e só quando existe de fato — ver `login.md`. */
  incidente?: Incidente;
  aoSair?: () => void;
  aoTentarDeNovo?: () => void;
};

function Glifo({
  nome,
  tamanho = 18,
}: {
  nome: 'zap' | 'seta' | 'volta' | 'escudo';
  tamanho?: number;
}): ReactNode {
  return (
    <svg
      width={tamanho}
      height={tamanho}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {nome === 'zap' ? (
        <path d="M10 3.2c3.9 0 7 2.7 7 6.1s-3.1 6.1-7 6.1c-.8 0-1.6-.1-2.3-.3L4 16.5l.9-2.9C3.7 12.5 3 11 3 9.3c0-3.4 3.1-6.1 7-6.1Z" />
      ) : null}
      {nome === 'seta' ? (
        <>
          <path d="M4 10h11" />
          <path d="m10.5 5.5 4.5 4.5-4.5 4.5" />
        </>
      ) : null}
      {nome === 'volta' ? (
        <>
          <path d="M16 10H5" />
          <path d="m9.5 5.5-4.5 4.5 4.5 4.5" />
        </>
      ) : null}
      {nome === 'escudo' ? (
        <>
          <path d="M10 2.8 16 5v4.6c0 3.3-2.4 6.2-6 7.6-3.6-1.4-6-4.3-6-7.6V5l6-2.2Z" />
          <path d="m7.4 10 1.8 1.8 3.4-3.8" />
        </>
      ) : null}
    </svg>
  );
}

/**
 * A marca: o logo inteiro, e nada escrito ao lado — ele já diz CLIQ CRM.
 *
 * A linha da marca só aparece nas duas telas de entrada. Nos desfechos ela é
 * ruído: quem chegou ali tem um problema, não está conhecendo o produto.
 */
function Marca({ comLinha = false }: { comLinha?: boolean }): ReactNode {
  return (
    <div className="entrada__marca">
      <img className="entrada__logo" src="/marca/logo-cliqcrm.png" alt="CliqCRM" />
      {comLinha ? (
        <p className="entrada__marca-linha">Customer Lifecycle Intelligence &amp; Quality</p>
      ) : null}
    </div>
  );
}

/**
 * O painel de ajuda.
 *
 * Não é vitrine: quem olha para este lado é quem NÃO conseguiu entrar. Uma
 * ação de verdade em branco cheio, o resto como texto, e o que não se aperta
 * não parece botão.
 */
function Painel({ suporte }: { suporte: Suporte }): ReactNode {
  const temContato = suporte.whatsapp !== '' || suporte.telefone !== '' || suporte.email !== '';
  return (
    <aside className="entrada__painel">
      <img className="entrada__aguada" src="/marca/simbolo-cliqcrm.png" alt="" aria-hidden="true" />

      <div className="entrada__bloco">
        <p className="entrada__painel-titulo">Não conseguiu entrar?</p>
        <p className="entrada__painel-frase">Fale com o suporte</p>
        {suporte.horario !== '' ? <p className="entrada__painel-apoio">{suporte.horario}</p> : null}
      </div>

      {temContato ? (
        <div className="entrada__bloco">
          {suporte.whatsapp !== '' ? (
            <a className="entrada__contato" href={`https://wa.me/${suporte.whatsapp}`}>
              <Glifo nome="zap" /> {suporte.whatsappVisivel || suporte.whatsapp}
            </a>
          ) : null}
          <div className="entrada__linhas">
            {suporte.telefone !== '' ? (
              <span className="entrada__linha">
                Telefone{' '}
                <a href={`tel:${suporte.telefone.replace(/\D/g, '')}`}>{suporte.telefone}</a>
              </span>
            ) : null}
            {suporte.email !== '' ? (
              <span className="entrada__linha">
                E-mail <a href={`mailto:${suporte.email}`}>{suporte.email}</a>
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      <p className="entrada__explica">
        Quem libera acesso é o <strong>administrador da sua conta</strong>, não o suporte do
        CliqCRM.
      </p>

      <p className="entrada__rodape">
        Seu acesso é pessoal. Não empreste a senha, nem para quem trabalha com você.
      </p>
    </aside>
  );
}

function Quem({ pessoa }: { pessoa: Pessoa }): ReactNode {
  return (
    <div className="entrada__quem">
      <span className="entrada__quem-avatar" aria-hidden="true">
        {pessoa.iniciais}
      </span>
      <span>
        <span className="entrada__quem-nome">{pessoa.nome}</span>
        <span className="entrada__quem-apoio">{pessoa.apoio}</span>
      </span>
    </div>
  );
}

function Correlacao({ id }: { id: string }): ReactNode {
  return (
    <p className="entrada__correlacao">
      <span>Se falar com o suporte, passe este código:</span>
      <code>{id}</code>
      <button
        className="entrada__copiar"
        type="button"
        onClick={() => void navigator.clipboard?.writeText(id)}
      >
        copiar
      </button>
    </p>
  );
}

/**
 * A nota de onde a senha é digitada.
 *
 * Fica porque responde a uma desconfiança real: a pessoa clica em "Continuar" e
 * vai para um endereço que não é o do CRM. Sem o aviso, isso se parece com
 * golpe.
 */
function NotaDaSenha(): ReactNode {
  return (
    <p className="entrada__nota">
      O login acontece no provedor de identidade da sua conta.{' '}
      <strong>A senha não passa por esta tela.</strong>
    </p>
  );
}

export function Entrada(props: EntradaProps): ReactNode {
  const {
    situacao,
    suporte,
    revenda,
    aoContinuar,
    revendas,
    aoEscolher,
    entrando = null,
    indo = false,
    pessoa,
    destino,
    correlationId,
    detalhe,
    desativadoEm,
    incidente,
    aoSair,
    aoTentarDeNovo,
  } = props;

  return (
    <main className="entrada">
      <div className="entrada__lado">
        <div className="entrada__cartao">
          <Marca comLinha={situacao === 'entrar' || situacao === 'escolher'} />

          {/* O incidente vive no CARTÃO: é sobre o login, e o login é o cartão. */}
          {incidente ? (
            <div className="entrada__incidente" role="status">
              <span aria-hidden="true">⏱</span>
              <span>
                {incidente.texto}
                <span className="entrada__incidente-quando">{incidente.desde}</span>
              </span>
            </div>
          ) : null}

          {situacao === 'entrar' ? (
            <>
              <h1 className="entrada__titulo">Entrar na sua conta</h1>
              <p className="entrada__apoio">
                Você vai informar seu e-mail e sua senha. Depois, se tiver acesso a mais de uma
                organização, escolhe em qual entrar.
              </p>
              {/*
                NÃO há campo aqui, e a ausência é o ponto.
                O e-mail e a senha são digitados no provedor de identidade, nunca
                nesta tela — é o que faz a aplicação nunca ver a senha de
                ninguém. Este botão só abre o login.
              */}
              <div className="entrada__formulario">
                <button
                  className="entrada__botao"
                  type="button"
                  onClick={aoContinuar}
                  disabled={indo}
                  autoFocus
                >
                  {indo ? 'Abrindo o login…' : 'Entrar'} <Glifo nome="seta" />
                </button>
              </div>
              <NotaDaSenha />
            </>
          ) : null}

          {situacao === 'escolher' ? (
            <>
              <h1 className="entrada__titulo">Onde você quer entrar?</h1>
              <p className="entrada__apoio">
                Sua conta tem acesso a mais de uma organização. Dá para trocar depois, pelo menu,
                sem entrar de novo.
              </p>

              <Tabela
                rotulo="Organizações da sua conta"
                densa
                classe="entrada__tabela"
                chaveDe={(linha) => String(linha['chave'])}
                colunas={[
                  { chave: 'nome', rotulo: 'Organização' },
                  { chave: 'papel', rotulo: 'Seu papel' },
                  // O cabeçalho da coluna de ação seria ruído lido em voz alta.
                  { chave: 'acao', rotulo: 'Entrar', rotuloOculto: true },
                ]}
                linhas={(revendas ?? []).map((r) => ({
                  chave: r.id,
                  nome: r.nome,
                  papel: nomeDoPapel(r.papel),
                  acao:
                    r.status === 'suspenso' ? (
                      /*
                       * Suspenso APARECE, e não entra.
                       *
                       * Esconder faria a organização sumir sem explicação para
                       * quem foi suspenso nela e continua ativo em outra — e
                       * essa pessoa abriria um chamado que a tela podia ter
                       * respondido.
                       */
                      <span className="entrada__suspenso">acesso suspenso</span>
                    ) : (
                      <button
                        className="entrada__entrar-na-linha"
                        type="button"
                        disabled={entrando !== null}
                        onClick={() => aoEscolher?.(r.slug)}
                      >
                        {entrando === r.slug ? 'entrando…' : 'Entrar'}
                      </button>
                    ),
                }))}
              />

              {/*
                Quem errou a conta percebe AQUI. Sem esta saída o único caminho
                seria fechar o navegador: o cookie do provedor sobreviveria, e o
                "entrar" seguinte voltaria à mesma conta sem pedir senha.
              */}
              {aoSair ? (
                <button className="entrada__secundario" type="button" onClick={aoSair}>
                  Entrar com outra conta
                </button>
              ) : null}
            </>
          ) : null}

          {situacao === 'voltando' ? (
            <>
              <h1 className="entrada__titulo">Só um instante</h1>
              <div className="entrada__espera" role="status">
                <span className="entrada__giro" aria-hidden="true" />
                <span>Confirmando seu acesso{revenda ? ` na ${revenda}` : ''}…</span>
              </div>
              {/* Esta é a tela em que dá para ficar preso sem nenhuma saída. */}
              <p className="entrada__apoio">
                Se esta tela não sair daqui em alguns segundos, o botão abaixo recomeça o login.
              </p>
              <button className="entrada__secundario" type="button" onClick={aoTentarDeNovo}>
                Recomeçar o login
              </button>
            </>
          ) : null}

          {situacao === 'revenda-nao-existe' ? (
            <>
              <span className="entrada__marcador entrada__marcador--neutro" aria-hidden="true">
                ?
              </span>
              <h1 className="entrada__titulo">Conta não encontrada</h1>
              <p className="entrada__apoio">
                Não existe conta com o identificador <strong>{revenda}</strong>. Confira a escrita —
                ele costuma ser curto, sem espaço e sem acento.
              </p>
              <div className="entrada__acoes">
                <button className="entrada__botao" type="button" onClick={aoTentarDeNovo}>
                  <Glifo nome="volta" /> Tentar outro identificador
                </button>
              </div>
              <p className="entrada__nota">
                Não sabe qual é o da sua conta? Ele aparece no endereço que a sua equipe usa para
                entrar. O administrador da conta também sabe.
              </p>
            </>
          ) : null}

          {situacao === 'provedor-fora' ? (
            <>
              <span className="entrada__marcador entrada__marcador--erro" aria-hidden="true">
                !
              </span>
              <h1 className="entrada__titulo">O login está fora do ar</h1>
              {/* Começa dizendo o que NÃO é: sem isso a pessoa tenta trocar a
                  senha e piora a própria situação. */}
              <p className="entrada__apoio">
                Não é problema com a sua senha nem com o seu acesso: o serviço que confirma sua
                identidade não respondeu. Costuma ser passageiro.
              </p>
              <div className="entrada__acoes">
                <button className="entrada__botao" type="button" onClick={aoTentarDeNovo}>
                  Tentar de novo
                </button>
              </div>
              {correlationId ? <Correlacao id={correlationId} /> : null}
              {detalhe ? <p className="entrada__nota">Detalhe técnico: {detalhe}</p> : null}
            </>
          ) : null}

          {situacao === 'sessao-expirada' ? (
            <>
              <h1 className="entrada__titulo">Sua sessão expirou</h1>
              <p className="entrada__apoio">
                Por segurança, o acesso expira depois de um tempo sem uso. Entre de novo para
                continuar.
              </p>
              {/* Quem estava logado responde à pergunta do momento: caiu, ou
                  alguém trocou de conta? */}
              {pessoa ? <Quem pessoa={pessoa} /> : null}
              {destino ? (
                <p className="entrada__destino">
                  <Glifo nome="volta" tamanho={16} />
                  <span>
                    Você volta para <strong>{destino}</strong>, onde estava.
                  </span>
                </p>
              ) : null}
              <div className="entrada__acoes">
                <button className="entrada__botao" type="button" onClick={aoContinuar}>
                  Entrar de novo
                </button>
                {aoSair ? (
                  <button className="entrada__secundario" type="button" onClick={aoSair}>
                    Entrar com outra conta
                  </button>
                ) : null}
              </div>
              <p className="entrada__nota">
                O que você estava escrevendo foi guardado neste navegador.
              </p>
            </>
          ) : null}

          {situacao === 'sem-vinculo' ? (
            <>
              <span className="entrada__marcador entrada__marcador--neutro" aria-hidden="true">
                ·
              </span>
              <h1 className="entrada__titulo">Seu acesso ainda não foi liberado</h1>
              <p className="entrada__apoio">
                Sua identidade foi confirmada, mas você ainda não está vinculado a nenhuma conta no
                CliqCRM. Quem libera é o administrador da sua conta.
              </p>
              {pessoa ? <Quem pessoa={pessoa} /> : null}
              <div className="entrada__acoes">
                <button className="entrada__secundario" type="button" onClick={aoTentarDeNovo}>
                  Verificar de novo
                </button>
                {aoSair ? (
                  <button className="entrada__secundario" type="button" onClick={aoSair}>
                    Sair desta conta
                  </button>
                ) : null}
              </div>
            </>
          ) : null}

          {/* Separada de `sem-vinculo` de propósito: nunca ter tido acesso e ter
              PERDIDO o acesso são situações diferentes. Juntá-las faria alguém
              desligado achar que é erro de sistema. */}
          {situacao === 'desativado' ? (
            <>
              <span className="entrada__marcador entrada__marcador--erro" aria-hidden="true">
                —
              </span>
              <h1 className="entrada__titulo">Seu acesso foi desativado</h1>
              <p className="entrada__apoio">
                Seu acesso à conta <strong>{revenda}</strong> foi desativado
                {desativadoEm ? ` em ${desativadoEm}` : ''}. Se isso não era esperado, fale com o
                administrador da conta.
              </p>
              <div className="entrada__acoes">
                {aoSair ? (
                  <button className="entrada__secundario" type="button" onClick={aoSair}>
                    Sair desta conta
                  </button>
                ) : null}
              </div>
              <p className="entrada__nota">
                Suas conversas e oportunidades continuam na conta, com quem assumiu o seu lugar.
              </p>
            </>
          ) : null}

          {situacao === 'saiu' ? (
            <>
              <span className="entrada__marcador entrada__marcador--ok" aria-hidden="true">
                <Glifo nome="escudo" tamanho={22} />
              </span>
              <h1 className="entrada__titulo">Você saiu</h1>
              {/* Diz que encerrou TAMBÉM no provedor: sem isso, "sair" num
                  computador compartilhado deixa a sessão viva do outro lado. */}
              <p className="entrada__apoio">
                Sua sessão foi encerrada neste navegador e também no provedor de identidade. Se este
                computador é compartilhado, pode fechar a aba.
              </p>
              <div className="entrada__acoes">
                <button className="entrada__botao" type="button" onClick={aoContinuar}>
                  Entrar de novo
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <Painel suporte={suporte} />
    </main>
  );
}
